import {
  Download,
  Expand,
  Filter,
  Focus,
  Maximize2,
  Menu,
  Network,
  RotateCcw,
  Search,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deriveVisibleGraph, parseGraphHash, searchGraph, serializeGraphHash, type GraphSelection } from "./app/graphView";
import { EmptyDetail, NodeDetail } from "./components/NodeDetail";
import { GraphCanvas, type GraphCanvasHandle } from "./components/GraphCanvas";
import { ReaderDialog } from "./components/ReaderDialog";
import type { GraphCore, GraphNodeRecord, TeachingSectionName, TeachingUnitRecord } from "./data/buildGraphData";
import "./styles.css";

type Position = { x: number; y: number };
type ContentModule = TeachingUnitRecord & { id: string };

const relationLabels = {
  prerequisite: "前置依赖",
  method_support: "方法支撑",
  application: "应用落地",
  falsification: "证伪约束",
};

const allRelations = new Set(Object.keys(relationLabels));

function selectionForNode(node: GraphNodeRecord): GraphSelection {
  if (node.kind === "module") return { type: "module", id: node.id };
  if (node.kind === "question") return { type: "question", id: node.id };
  if (node.kind === "knowledge") return { type: "node", id: node.id };
  if (node.kind === "submodule") return { type: "submodule", id: node.id };
  return { type: "module", id: node.moduleId ?? "module-1" };
}

function useHashSelection() {
  const [selection, setSelectionState] = useState<GraphSelection>(() => parseGraphHash(location.hash));
  useEffect(() => {
    const sync = () => setSelectionState(parseGraphHash(location.hash));
    addEventListener("hashchange", sync);
    if (!location.hash) location.hash = "#overview";
    return () => removeEventListener("hashchange", sync);
  }, []);
  const setSelection = useCallback((next: GraphSelection) => {
    const hash = serializeGraphHash(next);
    if (location.hash === hash) setSelectionState(next);
    else location.hash = hash;
  }, []);
  return [selection, setSelection] as const;
}

export default function App() {
  const [graph, setGraph] = useState<GraphCore>();
  const [positions, setPositions] = useState<Record<string, Position>>({});
  const [selection, setSelection] = useHashSelection();
  const [query, setQuery] = useState("");
  const [selectedUnit, setSelectedUnit] = useState<TeachingUnitRecord>();
  const [readerSection, setReaderSection] = useState<TeachingSectionName>();
  const [relations, setRelations] = useState<Set<string>>(allRelations);
  const [status, setStatus] = useState("拖动画布探索；悬停语义边查看审核理由。");
  const [mobileNav, setMobileNav] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [presentationIndex, setPresentationIndex] = useState<number>();
  const contentCache = useRef(new Map<number, ContentModule[]>());
  const graphRef = useRef<GraphCanvasHandle>(null);

  useEffect(() => {
    Promise.all([
      fetch("./data/graph-core.json").then((response) => response.json() as Promise<GraphCore>),
      fetch("./data/layout.json").then((response) => response.json() as Promise<Record<string, Position>>),
    ]).then(([nextGraph, nextPositions]) => {
      setGraph(nextGraph);
      setPositions(nextPositions);
    }).catch((error: unknown) => setStatus(`数据加载失败：${String(error)}`));
  }, []);

  const loadUnit = useCallback(async (node: GraphNodeRecord) => {
    if (node.kind !== "knowledge" || !node.moduleId) return;
    const moduleNumber = Number(node.moduleId.replace("module-", ""));
    let contents: ContentModule[] | undefined = contentCache.current.get(moduleNumber);
    if (!contents) {
      const loaded = await fetch(`./data/content/module-${moduleNumber}.json`).then(
        (response) => response.json() as Promise<ContentModule[]>,
      );
      contentCache.current.set(moduleNumber, loaded);
      contents = loaded;
    }
    const unit = contents.find((candidate) => candidate.number === node.number);
    if (unit) {
      setSelectedUnit(unit);
      setDetailsOpen(true);
    }
  }, []);

  useEffect(() => {
    if (!graph || selection.type !== "node") return;
    const node = graph.nodes.find((candidate) => candidate.id === selection.id);
    if (node) void loadUnit(node);
  }, [graph, loadUnit, selection]);

  const visibleGraph = useMemo(
    () => graph ? deriveVisibleGraph(graph, selection) : undefined,
    [graph, selection],
  );
  const results = useMemo(() => graph ? searchGraph(graph, query) : [], [graph, query]);

  const selectNode = useCallback((node: GraphNodeRecord) => {
    setSelection(selectionForNode(node));
    setQuery("");
    setMobileNav(false);
    if (node.kind === "knowledge") void loadUnit(node);
  }, [loadUnit, setSelection]);

  const startPresentation = () => {
    if (!graph) return;
    setPresentationIndex(0);
    setSelection({ type: "stage", id: graph.stages[0]!.id });
  };
  const movePresentation = (direction: number) => {
    if (!graph || presentationIndex === undefined) return;
    const next = Math.min(5, Math.max(0, presentationIndex + direction));
    setPresentationIndex(next);
    setSelection({ type: "stage", id: graph.stages[next]!.id });
  };

  if (!graph || !visibleGraph) {
    return <main className="loading"><Network size={26} /><p>正在装载知识图谱…</p></main>;
  }

  return (
    <main className={`app-shell ${selectedUnit && detailsOpen ? "has-detail" : ""}`}>
      <header className="topbar">
        <button className="mobile-menu" type="button" onClick={() => setMobileNav(true)} aria-label="打开阶段导航"><Menu size={20} /></button>
        <div className="brand">
          <Network size={21} aria-hidden="true" />
          <div><strong>量化研究知识图谱</strong><span>6 阶段 · 45 问 · 675 节点</span></div>
        </div>
        <div className="search-box">
          <Search size={17} aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索编号、问题或知识点" aria-label="搜索知识图谱" />
          {query && <button type="button" onClick={() => setQuery("")} aria-label="清空搜索"><X size={15} /></button>}
          {query && (
            <div className="search-results" role="listbox">
              {results.length ? results.map((node) => (
                <button key={node.id} type="button" onClick={() => selectNode(node)} role="option">
                  <span>{node.kind === "knowledge" ? `N${node.number}` : node.code ?? node.id}</span>
                  <strong>{node.label}</strong>
                </button>
              )) : <p>未找到匹配节点</p>}
            </div>
          )}
        </div>
        <a className="artifact-link" href="./artifacts/index.html" title="下载静态图包"><Download size={18} /><span>图包</span></a>
      </header>

      <aside className={`left-panel ${mobileNav ? "is-open" : ""}`}>
        <div className="panel-mobile-heading"><strong>六阶段认知主线</strong><button type="button" onClick={() => setMobileNav(false)} aria-label="关闭导航"><X size={18} /></button></div>
        <button className={selection.type === "overview" ? "overview-button is-active" : "overview-button"} type="button" onClick={() => setSelection({ type: "overview", id: "overview" })}>
          <Focus size={16} />总览：阶段、模块与子模块
        </button>
        <nav aria-label="六阶段与四十五问">
          {graph.stages.map((stage) => (
            <section key={stage.id} className={selection.id === stage.id ? "stage-group is-active" : "stage-group"}>
              <button type="button" className="stage-button" onClick={() => setSelection({ type: "stage", id: stage.id })}>
                <span>0{stage.number}</span><strong>{stage.title}</strong>
              </button>
              <div className="question-list">
                {stage.questionIds.map((questionId) => {
                  const question = graph.questions.find((candidate) => candidate.id === questionId)!;
                  return <button key={questionId} type="button" className={selection.id === questionId ? "is-active" : ""} onClick={() => setSelection({ type: "question", id: questionId })}><span>{questionId}</span>{question.title}</button>;
                })}
              </div>
            </section>
          ))}
        </nav>
      </aside>

      <section className="graph-workspace">
        <div className="graph-toolbar" aria-label="图谱工具栏">
          <div className="view-label">
            <span>{selection.type === "overview" ? "结构总览" : selection.type === "all" ? "全量网络" : "聚焦视图"}</span>
            <strong>{visibleGraph.nodes.length} 节点 / {visibleGraph.edges.length} 关系</strong>
          </div>
          <div className="filter-menu">
            <button type="button" aria-label="关系筛选" title="关系筛选"><Filter size={17} /></button>
            <div className="filter-popover">
              {Object.entries(relationLabels).map(([key, label]) => (
                <label key={key}><input type="checkbox" checked={relations.has(key)} onChange={() => setRelations((current) => { const next = new Set(current); if (next.has(key)) next.delete(key); else next.add(key); return next; })} /><span className={`legend-dot ${key}`} />{label}</label>
              ))}
            </div>
          </div>
          <button type="button" onClick={() => setSelection({ type: "all", id: "all" })} aria-label="显示全部节点" title="显示全部节点"><Expand size={17} /></button>
          <button type="button" onClick={() => graphRef.current?.relayout()} aria-label="重排当前视图" title="重排当前视图"><Network size={17} /></button>
          <button type="button" onClick={() => graphRef.current?.reset()} aria-label="恢复稳定布局" title="恢复稳定布局"><RotateCcw size={17} /></button>
          <button type="button" onClick={() => graphRef.current?.toggleFullscreen()} aria-label="全屏画布" title="全屏画布"><Maximize2 size={17} /></button>
          <button type="button" onClick={() => graphRef.current?.exportPng()} aria-label="导出当前视图" title="导出当前视图"><Download size={17} /></button>
        </div>
        <GraphCanvas ref={graphRef} graph={visibleGraph} positions={positions} selectedId={selection.type === "node" ? selection.id : undefined} relationKinds={relations} refineLayout={!(["overview", "all"] as string[]).includes(selection.type)} onSelect={selectNode} onStatus={setStatus} />
        <div className="graph-status"><span>{status}</span><button type="button" onClick={startPresentation}>六阶段演示</button></div>
        {presentationIndex !== undefined && (
          <div className="presentation-bar">
            <button type="button" onClick={() => movePresentation(-1)} disabled={presentationIndex === 0}>上一阶段</button>
            <span>{presentationIndex + 1} / 6 · {graph.stages[presentationIndex]?.title}</span>
            <button type="button" onClick={() => movePresentation(1)} disabled={presentationIndex === 5}>下一阶段</button>
            <button type="button" onClick={() => setPresentationIndex(undefined)} aria-label="结束演示"><X size={16} /></button>
          </div>
        )}
      </section>

      <aside className={`right-panel ${detailsOpen ? "is-open" : ""}`}>
        <button className="mobile-detail-close" type="button" onClick={() => setDetailsOpen(false)} aria-label="关闭节点详情"><X size={18} /></button>
        {selectedUnit ? <NodeDetail unit={selectedUnit} onOpenReader={setReaderSection} /> : <EmptyDetail />}
      </aside>

      {selectedUnit && readerSection && <ReaderDialog unit={selectedUnit} initialSection={readerSection} onClose={() => setReaderSection(undefined)} />}
      {(mobileNav || detailsOpen) && <button className="mobile-backdrop" type="button" onClick={() => { setMobileNav(false); setDetailsOpen(false); }} aria-label="关闭面板" />}
    </main>
  );
}
